import { useEffect } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  handler: (e: KeyboardEvent) => void;
  description?: string;
}

/**
 * Hook to register keyboard shortcuts
 * Handles both Cmd (Mac) and Ctrl (Windows/Linux)
 */
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[], enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      for (const shortcut of shortcuts) {
        const isCtrlOrCmd = shortcut.ctrlKey || shortcut.metaKey;
        const ctrlMatch = isCtrlOrCmd ? e.ctrlKey || e.metaKey : !e.ctrlKey && !e.metaKey;
        const shiftMatch = shortcut.shiftKey ? e.shiftKey : !e.shiftKey;
        const altMatch = shortcut.altKey ? e.altKey : !e.altKey;

        if (
          e.key.toLowerCase() === shortcut.key.toLowerCase() &&
          ctrlMatch &&
          shiftMatch &&
          altMatch
        ) {
          e.preventDefault();
          shortcut.handler(e);
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, enabled]);
}

/**
 * Common keyboard shortcuts for module editors
 */
export const createEditorShortcuts = (onSave?: () => void, onCancel?: () => void) => {
  const shortcuts: KeyboardShortcut[] = [];

  if (onSave) {
    shortcuts.push({
      key: 's',
      ctrlKey: true,
      metaKey: true,
      handler: (e) => {
        e.preventDefault();
        onSave();
      },
      description: 'Save (Cmd/Ctrl + S)',
    });
  }

  if (onCancel) {
    shortcuts.push({
      key: 'Escape',
      handler: (e) => {
        e.preventDefault();
        onCancel();
      },
      description: 'Cancel (Esc)',
    });
  }

  return shortcuts;
};

/**
 * Rich text editor keyboard shortcuts
 */
export const createRichTextShortcuts = (editor: any) => {
  if (!editor) return [];

  return [
    {
      key: 'b',
      ctrlKey: true,
      metaKey: true,
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        editor.chain().focus().toggleBold().run();
      },
      description: 'Bold (Cmd/Ctrl + B)',
    },
    {
      key: 'i',
      ctrlKey: true,
      metaKey: true,
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        editor.chain().focus().toggleItalic().run();
      },
      description: 'Italic (Cmd/Ctrl + I)',
    },
    {
      key: 'u',
      ctrlKey: true,
      metaKey: true,
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        editor.chain().focus().toggleUnderline().run();
      },
      description: 'Underline (Cmd/Ctrl + U)',
    },
    {
      key: 'k',
      ctrlKey: true,
      metaKey: true,
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        const url = window.prompt('Enter URL:');
        if (url) {
          editor.chain().focus().setLink({ href: url }).run();
        }
      },
      description: 'Insert Link (Cmd/Ctrl + K)',
    },
  ];
};
